import { useCallback } from "react";
import { useLocation, useNavigate, type To } from "react-router-dom";

// ============================================================================
// Site-wide "go back where I came from" (NAV-03).
//
// The problem this solves: detail pages used to guess where Back should go from
// a small set of ?from= values, and any link that didn't set ?from= fell back
// to a hardcoded default (e.g. the dispatch page always said "Back to board").
// So clicking a dispatch from the agreements tab sent you "back to board" — a
// place you were never at.
//
// The fix: when a smart-link navigates, it records the screen you're LEAVING
// (its full URL + a human label) in router location state. The destination
// reads that and returns there. No per-link wiring, works from anywhere.
//
// location.state is lost on a hard refresh / direct URL entry — that's fine,
// the destination falls back to a sensible default in that case.
// ============================================================================

export interface BackTarget {
  url: string;
  label: string; // rendered as "Back to {label}"
}

// Human label for the screen at this path — what "Back to {label}" should read.
export function deriveBackLabel(pathname: string, search: string): string {
  if (pathname.startsWith("/customers/")) {
    // On a customer record, prefer the tab they were on so it reads
    // "Back to agreements" / "Back to equipment", not a generic "customer".
    const tab = new URLSearchParams(search).get("tab");
    const tabLabel: Record<string, string> = {
      overview: "customer",
      dispatches: "service",
      equipment: "equipment",
      agreements: "agreements",
      notes: "notes",
    };
    if (tab && tabLabel[tab]) return tabLabel[tab];
    return "customer";
  }
  if (pathname.startsWith("/customers")) return "search"; // list/search page
  if (pathname.startsWith("/team")) {
    const pane = new URLSearchParams(search).get("pane");
    const paneLabel: Record<string, string> = {
      week: "Install Week",
      scoreboard: "Scoreboard",
      library: "Library",
      messages: "Messages",
    };
    return (pane && paneLabel[pane]) || "Team";
  }
  if (pathname.startsWith("/agreements")) return "agreements";
  if (pathname.startsWith("/reports")) return "reports";
  if (pathname.startsWith("/board")) return "board";
  if (pathname.startsWith("/today")) return "today";
  if (pathname.startsWith("/inbox")) return "inbox";
  if (pathname.startsWith("/dispatch/")) return "dispatch";
  if (pathname.startsWith("/invoice/")) return "invoice";
  return "back";
}

/** "Back to agreements" / "Back to board" — but just "Back" for unknowns. */
export function backText(label: string): string {
  return label === "back" ? "Back" : `Back to ${label}`;
}

/**
 * Navigate while recording the current screen as the back target. Use this in
 * place of useNavigate() anywhere a click drills INTO a detail page.
 */
export function useNavigateWithBack() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (to: To) => {
      const url = location.pathname + location.search;
      const label = deriveBackLabel(location.pathname, location.search);
      navigate(to, { state: { back: { url, label } satisfies BackTarget } });
    },
    [navigate, location.pathname, location.search],
  );
}

/**
 * Read the back target the previous screen recorded. Falls back to the given
 * default when there's none (hard refresh, direct link, or an old-style ?from=
 * caller the destination still computes a default for).
 */
export function useBackTarget(fallback: BackTarget): BackTarget {
  const location = useLocation();
  const back = (location.state as { back?: BackTarget } | null)?.back;
  return back ?? fallback;
}
